import React, { FC, FormEvent, MouseEvent, useState } from "react";

import {
  useAiChatConversationRequest,
  useApisMembersAiChatRoomsAiEmployeeConversationsIndex,
  useArray,
  useInput,
  useProvidersCurrentEmployee,
  useApiPolling,
  useBoolean,
} from "~/hooks";

import { ChatDetail } from "./ChatDetail";
import { FloatingChatBoxHeader } from "../FloatingChatBoxHeader";

import { AiEmployeeConversationType, AiChatRoomSettingType } from "~/domains";

import { AiEmployeeConversationSender } from "~/domains/AiChatRoom";
import { IconButtonWithTextArea, InfiniteScroll } from "~/ui";
import { Form, DotLoadingAnimation } from "~/ui";

type PropsType = {
  aiChatRoomId: string;
  onClose: (e: MouseEvent<HTMLButtonElement>) => void;
  setAiChatRoomId: (chatRoomId: null) => void;
};

export const FloatingChatForm: FC<PropsType> = ({
  aiChatRoomId,
  onClose,
  setAiChatRoomId,
}: PropsType) => {
  const { currentEmployee } = useProvidersCurrentEmployee();

  const [{ value, onChange }, setValue] = useInput("");
  const { items, setItems, unshiftItem } =
    useArray<AiEmployeeConversationType>();
  const [aiChatRoomSetting, setAiChatRoomSetting] =
    useState<AiChatRoomSettingType>();
  const {
    isChecked: isWaitingForAiResponse,
    setTrue: startWaitingForAiResponse,
    setFalse: stopWaitingForAiResponse,
    handleChange: setIsWaitingForAiResponse,
  } = useBoolean(false);
  const { aiChatConversationRequest, isEmployeeSubmitting } =
    useAiChatConversationRequest();
  const { fetchNextPage, hasNextPage, isLoading, refetch } =
    useApisMembersAiChatRoomsAiEmployeeConversationsIndex({
      aiChatRoomId,
      config: {
        onSuccess: (data) => {
          data.pages[0]?.aiChatRoom.aiChatRoomSetting &&
            setAiChatRoomSetting(data.pages[0].aiChatRoom.aiChatRoomSetting);
          const conversations = data.pages
            .map((page) => page.aiEmployeeConversations)
            .flat();
          setItems(conversations);
          const latestConversation = conversations[0];
          setIsWaitingForAiResponse(
            latestConversation?.senderType?.type ===
              AiEmployeeConversationSender.user.type,
          );
        },
      },
    });

  useApiPolling({
    enabled: isWaitingForAiResponse,
    interval: 2000,
    pollingFunction: () => {
      void refetch();
    },
    onTimeout: () => {
      stopWaitingForAiResponse();
    },
    timeoutDuration: 30000,
  });

  const handleSetAiChatRoomId = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setAiChatRoomId(null);
  };

  const handleSubmit = (
    e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();

    aiChatConversationRequest({
      message: value,
      aiChatRoomId,
      onSuccess: (response) => {
        unshiftItem(response.aiEmployeeConversation);
        setValue("");
        startWaitingForAiResponse();
      },
    });
  };

  return (
    <div className="flex flex-col h-full space-y-6">
      <div>
        <FloatingChatBoxHeader
          onClose={onClose}
          onBack={handleSetAiChatRoomId}
        />
        {aiChatRoomSetting && (
          <p className="text-center mt-2 text-sm text-secondary-600">
            {aiChatRoomSetting.expertRoleName}
          </p>
        )}
      </div>
      <InfiniteScroll
        itemsLength={items.length}
        nextFetchFunction={fetchNextPage}
        hasMore={hasNextPage}
        inverse
        isLoading={isLoading}
        noContentMessage="メッセージがありません"
      >
        {items.map((item) => (
          <ChatDetail
            key={item.id}
            conversation={item}
            currentEmployeeAvatarUrl={currentEmployee?.avatarUrl || ""}
            className="mb-4"
          />
        ))}
      </InfiniteScroll>
      {isWaitingForAiResponse && (
        <DotLoadingAnimation className="justify-center" />
      )}
      <Form
        onSubmit={handleSubmit}
        className="sticky bottom-0 flex flex-col grow justify-end"
      >
        <IconButtonWithTextArea
          value={value}
          onChange={onChange}
          placeholder="質問を入力してください"
          name="freeWord"
          rows={1}
          size="md"
          icon="ioSend"
          onClick={handleSubmit}
          disabled={isEmployeeSubmitting || isWaitingForAiResponse}
        />
      </Form>
    </div>
  );
};
